import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const Header = styled.div`
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
  padding: 4rem 1rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0rem 1rem 4rem;
  color: ${({ theme }) => theme.colors.black};
`;

const Section = styled.section`
  margin-bottom: 0rem;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const LastUpdated = styled.p`
  color: ${({ theme }) => theme.colors.darkGray};
  font-style: italic;
  margin-bottom: 2rem;
`;

const Paragraph = styled.p`
  margin-bottom: 0rem;
  line-height: 1.6;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 2rem;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const TermsOfServicePage: React.FC = () => {
  const pageTitle = 'Terms of Service | ElectroCable';
  const pageDescription = 'Review the Terms of Service for ElectroCable. Understand the conditions for using our website, products, and services. Your agreement to these terms is important.';
  const siteUrl = 'https://www.yourwebsite.com'; // Replace with your actual domain
  const termsPageUrl = `${siteUrl}/terms-of-service`;
  const ogImageUrl = `${siteUrl}/images/logo.svg`; // Or a generic legal/company OG image

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={termsPageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={termsPageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />
      </Helmet>

      <Header>
        <HeaderTitle>Terms of Service</HeaderTitle>
      </Header>

      <Container>
        <LastUpdated>Last Updated: April 2, 2025</LastUpdated>

        <Section>
          <Paragraph>
            These Terms of Service ("Terms") govern your access to and use of the ElectroCable website,
            products, and services. Please read these Terms carefully before using our services.
          </Paragraph>
          <Paragraph>
            By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy.
            If you do not agree to these Terms, please do not use our services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Use of Services</SectionTitle>
          <Paragraph>
            ElectroCable provides a platform for browsing and purchasing electrical wiring and cable products.
            You may use our services only as permitted by these Terms and applicable laws and regulations.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Account Registration</SectionTitle>
          <Paragraph>
            To access certain features of our services, you may need to register for an account.
            You agree to provide accurate, current, and complete information during the registration process
            and to update such information to keep it accurate, current, and complete.
          </Paragraph>
          <Paragraph>
            You are responsible for safeguarding your account and for all activities that occur under your account.
            You agree to notify ElectroCable immediately of any unauthorized use of your account.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3. Products and Orders</SectionTitle>
          <Paragraph>
            3.1 <strong>Product Information</strong>: We strive to display as accurately as possible the colors,
            features, specifications, and details of the products available on our website. However, we cannot
            guarantee that your computer's display of any color will be accurate, and we do not warrant that
            product descriptions or other content is accurate, complete, reliable, current, or error-free.
          </Paragraph>
          <Paragraph>
            3.2 <strong>Pricing and Availability</strong>: All prices are shown in USD and do not include taxes,
            shipping, or handling unless otherwise specified. We reserve the right to change prices at any time.
            The availability of products is subject to change without notice.
          </Paragraph>
          <Paragraph>
            3.3 <strong>Orders</strong>: Your order constitutes an offer to purchase the products. We reserve the
            right to accept or decline your order for any reason. After you place an order, you will receive an email
            confirmation with your order details.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Returns and Refunds</SectionTitle>
          <Paragraph>
            Our return and refund policy is designed to ensure your satisfaction with our products. Please refer to
            our Returns and Refunds page for detailed information about our policies and procedures.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Intellectual Property</SectionTitle>
          <Paragraph>
            The content, organization, graphics, design, compilation, and other matters related to our website are
            protected under applicable copyrights, trademarks, and other proprietary rights. Copying, redistribution,
            use, or publication of any such matters or any part of the website is prohibited without our express
            permission.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Limitation of Liability</SectionTitle>
          <Paragraph>
            To the maximum extent permitted by law, ElectroCable shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly
            or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:
          </Paragraph>
          <List>
            <li>Your access to or use of or inability to access or use the services;</li>
            <li>Any conduct or content of any third party on the services;</li>
            <li>Any content obtained from the services; or</li>
            <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>7. Governing Law</SectionTitle>
          <Paragraph>
            These Terms shall be governed by and construed in accordance with the laws of the state of Florida,
            without regard to its conflict of law provisions. You and ElectroCable agree to submit to the personal
            and exclusive jurisdiction of the courts located within Tamarac, Florida.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Changes to Terms</SectionTitle>
          <Paragraph>
            We reserve the right to modify these Terms at any time. If we make changes, we will provide notice of
            such changes, such as by sending an email notification, providing notice through our services, or updating
            the date at the top of these Terms. Your continued use of our services following the posting of revised
            Terms means that you accept and agree to the changes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about these Terms, please contact us at:
          </Paragraph>
          <Paragraph>
            <strong>Email:</strong> info@electrocableus.com<br />
            <strong>Phone:</strong> +1 847 376 9187<br />
            <strong>Address:</strong> 10408 West Mcnab Road Tamarac, FL 33321, United States
          </Paragraph>
        </Section>
      </Container>
    </>
  );
};

export default TermsOfServicePage; 